/**
 * Grid Debug Overlay
 * Affiche une grille visuelle basée sur --grid-margin, --gutter et --column-count
 */

class Grid {
  constructor() {
    this.overlay = null
    this.isVisible = false
    this.columnCount = this.getColumnCount()

    this.init()
    this.bindEvents()
  }


  getColumnCount() {
    const style = getComputedStyle(document.documentElement)
    return parseInt(style.getPropertyValue('--column-count') || '8')
  }


  getPx(property) {
    return parseFloat(getComputedStyle(document.documentElement).getPropertyValue(property)) || 0
  }


  computeColumnWidth() {
    const vw = document.documentElement.clientWidth
    const margin = this.getPx('--grid-margin')
    const gutter = this.getPx('--gutter')
    const count = this.getColumnCount()
    const column = (vw - margin * 2 - gutter * (count - 1)) / count
    document.documentElement.style.setProperty('--column', `${column}px`)
    this.updateInfoLabel()
  }


  init() {
    if (document.readyState === 'complete') {
      this.computeColumnWidth()
    } else {
      window.addEventListener('load', () => this.computeColumnWidth(), { once: true })
    }
    this.isVisible = true
    this.overlay = document.createElement('div')
    this.overlay.className = 'grid-overlay'
    this.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
      display: block;
    `

    this.infoLabel = document.createElement('div')
    this.infoLabel.className = 'grid-overlay__info'
    this.infoLabel.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 12px;
      font-family: monospace;
      z-index: 10000;
    `
    const gridContainer = document.createElement('div')
    gridContainer.className = 'grid-overlay__container'
    gridContainer.style.cssText = `
      width: 100%;
      height: 100%;
      display: flex;
    `

    this.updateColumns(gridContainer)

    this.overlay.appendChild(gridContainer)
    this.overlay.appendChild(this.infoLabel)
    document.body.appendChild(this.overlay)
  }


  updateInfoLabel() {
    const gutter = this.getPx('--gutter')
    const margin = this.getPx('--grid-margin')
    const column = this.getPx('--column')

    this.infoLabel.innerHTML = `
      <strong>Grid Info</strong><br>
      Columns: ${this.columnCount}<br>
      Column: ${column.toFixed(1)}px<br>
      Gutter: ${gutter}px<br>
      Margin: ${margin}px<br>
      Width: ${document.documentElement.clientWidth}px
    `
  }


  createColumn() {
    const column = document.createElement('span')
    column.className = 'grid-overlay__column'
    column.style.cssText = `
      flex: 1 1 auto;
      background: rgba(255, 0, 0, 0.1);
      height: 100%;
    `
    return column
  }


  createGutter() {
    const gutter = document.createElement('span')
    gutter.className = 'grid-overlay__gutter'
    gutter.style.cssText = `
      flex: 0 0 auto;
      width: var(--gutter, 20px);
      border-left: 1px solid rgba(0, 255, 255, 0.5);
      border-right: 1px solid rgba(0, 255, 255, 0.5);
      box-sizing: border-box;
      height: 100%;
    `
    return gutter
  }


  createMargin() {
    const margin = document.createElement('span')
    margin.className = 'grid-overlay__margin'
    margin.style.cssText = `
      flex: 0 0 auto;
      width: var(--grid-margin, 0px);
      background: rgba(0, 255, 0, 0.08);
      border-right: 1px dashed rgba(0, 255, 0, 0.4);
      box-sizing: border-box;
      height: 100%;
    `
    return margin
  }


  createMarginRight() {
    const margin = this.createMargin()
    margin.style.borderRight = 'none'
    margin.style.borderLeft = '1px dashed rgba(0, 255, 0, 0.4)'
    return margin
  }


  updateColumns(container) {
    container.innerHTML = ''
    this.columnCount = this.getColumnCount()

    // Pattern: [margin] [col] [gutter] [col] [gutter] ... [col] [margin]
    container.appendChild(this.createMargin())

    for (let i = 0; i < this.columnCount; i++) {
      container.appendChild(this.createColumn())

      if (i < this.columnCount - 1) {
        container.appendChild(this.createGutter())
      }
    }

    container.appendChild(this.createMarginRight())
  }


  bindEvents() {
    window.addEventListener('resize', () => {
      this.computeColumnWidth()
      const newColumnCount = this.getColumnCount()
      if (newColumnCount !== this.columnCount) {
        const container = this.overlay.querySelector('.grid-overlay__container')
        this.updateColumns(container)
      }
      this.updateInfoLabel()
    })

    document.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
        e.preventDefault()
        this.toggle()
      }
    })
  }


  show() {
    this.isVisible = true
    this.overlay.style.display = 'block'
  }


  hide() {
    this.isVisible = false
    this.overlay.style.display = 'none'
  }


  toggle() {
    this.isVisible ? this.hide() : this.show()
  }


  destroy() {
    if (this.overlay) {
      this.overlay.remove()
      this.overlay = null
    }
  }
}


// Grid() mounts DOM/listeners on construction — only instantiate it in dev,
// otherwise the overlay would leak into production for every visitor.
const grid = import.meta.env.DEV
  ? new Grid()
  : { toggle() {}, show() {}, hide() {}, destroy() {} }

export default grid
